import { Button, ConfirmationModalContent, ModalPopup, Stack } from 'components';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAddendums, getUsersImageNotes } from 'store/clientProfile/clientProfileActions';
import {
  Addendum as AddendumType,
  ClientProfileContentItem
} from 'store/clientProfile/clientProfileSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useIsOnlyVisibleToUserWithPermissions } from 'utils/hooks/useIsOnlyVisibleTo';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';

import { FinalNoteForm } from '../noteEdit/presets/FinalNoteForm';
import { NoteViewContent } from './NoteViewDisplay';
import style from './noteView.module.scss';
import { Addendum } from './addendum';

type Props = {
  note: ClientProfileContentItem;
};

const buildAddendumsText = (addendums: AddendumType[]) => {
  if (!addendums.length) return '';

  const sortedAddendums = [...addendums].sort(
    (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf()
  );

  return sortedAddendums
    .map((addendum) => {
      const authorName = addendum.author?.name ?? '';
      const date = dayjs(addendum.createdAt).isValid()
        ? dayjs(addendum.createdAt).format('MM/DD/YYYY')
        : '';
      const header = `Addendum (${authorName} | ${date}):`;
      const content = addendum.content ?? '';

      return `${header}\n${content}`.trimEnd();
    })
    .join('\n\n');
};

const NoteView = ({ note }: Props) => {
  const addendums = useAppSelector((state) => state.clientProfile.addendums.data);
  const { clientId = '' } = useParams();
  const dispatch = useAppDispatch();
  const sitesEnabled = useAppSelector((state) => state.clients.client.data?.enableProgramSites);
  const [isEditing, setIsEditing] = useState(false);
  const [showEditConfirmation, setShowEditConfirmation] = useState(false);

  const currentUserId = getLsUserRoleObject()?.userId;
  const hasEditAllPermission = useIsOnlyVisibleToUserWithPermissions(['edit_all_finalized_notes']);

  const showAddendums = addendums.length > 0 && (note.noteHistory?.length ?? 0) < 1;

  const isNoteCreator = currentUserId && note.createdBy === currentUserId;
  const canEditNote = isNoteCreator || hasEditAllPermission;

  useEffect(() => {
    dispatch(getAddendums({ noteId: note.id.toString() })).then((res: any) => {
      if (!res.error && res.payload.content.length > 0) {
        //TODO need to optimize
        res.payload.content.map((addendum: AddendumType) => {
          if (addendum.author.profileImageKey) {
            dispatch(
              getUsersImageNotes({
                profileImageKey: addendum.author.profileImageKey,
                noteId: addendum.id.toString(),
                noteAddendums: true
              })
            );
          }
        });
      }
    });
  }, []);

  useEffect(() => {
    if (!isEditing) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEditing]);

  if (isEditing) {
    const addendumsText = buildAddendumsText(addendums);
    const noteText = note?.note ? note.note.trimEnd() : '';
    const noteWithAddendums = addendumsText
      ? {
          ...note,
          note: [noteText, addendumsText].filter(Boolean).join('\n\n')
        }
      : note;

    return (
      <FinalNoteForm
        clientId={clientId}
        note={noteWithAddendums}
        onCancel={() => setIsEditing(false)}
        onSubmitSuccess={() => setIsEditing(false)}
      />
    );
  }

  return (
    <Stack mt="36px">
      <NoteViewContent note={note} sitesEnabled={sitesEnabled} />
      {canEditNote && (
        <Button
          text=""
          type="button"
          customStyleClass={style.actionButton}
          icon={<span className={'material-icons'}>edit</span>}
          handleOnClick={() => setShowEditConfirmation(true)}
        />
      )}
      {showAddendums &&
        addendums.map((addendum) => (
          <Addendum key={addendum.id} addendum={addendum} editable={false} />
        ))}

      <ModalPopup open={showEditConfirmation} handleClose={() => setShowEditConfirmation(false)}>
        <ConfirmationModalContent
          title="Are you sure you want to edit this finalized note?"
          text=" "
          confirmButtonText="Yes, I'm sure"
          rejectButtonText="No"
          handleConfirm={() => {
            setShowEditConfirmation(false);
            setIsEditing(true);
          }}
          handleReject={() => setShowEditConfirmation(false)}
        />
      </ModalPopup>
    </Stack>
  );
};

export default NoteView;
